import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { ContractType } from 'src/app/_enums/contract-type.enum';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import { InsuranceType } from 'src/app/_enums/insurance-type.enum';
import { fmtDate } from 'src/app/_helpers/utils';
import { DocumentService } from 'src/app/_services/document.service';
import { SignedContractDocument } from 'src/app/_types/document.types';
import { ComboboxComponent } from 'src/app/shared/components/combobox/combobox.component';
import {
  DOCUMENT_COMBOBOX_STYLES,
  DOCUMENT_COMBOBOX_TEMPLATE,
  DocumentComboboxBase,
} from 'src/app/shared/components/document-combobox/document-combobox.base';
import { DocumentUploadModalComponent } from 'src/app/shared/components/document-upload-modal/document-upload-modal.component';

/** Same names on both enums; the upload form speaks `DocumentType`. */
const UPLOAD_TYPES: Record<ContractType, DocumentType> = {
  [ContractType.ARTICLE_45]: DocumentType.ARTICLE_45,
  [ContractType.MANAGEMENT_AGREEMENT]: DocumentType.MANAGEMENT_AGREEMENT,
};

/** Picking it lands `null` in the control; see `DocumentComboboxBase.sentinel`. */
export const NO_SIGNED_CONTRACT_DOCUMENT = {
  documentId: -1,
  docName: '',
  docUrl: '',
  createdOn: '',
  signedOn: '',
} as unknown as SignedContractDocument;

@Component({
  selector: 'app-signed-contract-document-combobox',
  standalone: true,
  imports: [ComboboxComponent, DocumentUploadModalComponent],
  template: DOCUMENT_COMBOBOX_TEMPLATE,
  styles: [DOCUMENT_COMBOBOX_STYLES],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignedContractDocumentComboboxComponent extends DocumentComboboxBase<SignedContractDocument> {
  private readonly documentService = inject(DocumentService);

  readonly familyMemberId = input<string | null>(null);

  readonly contractType = input.required<ContractType>();

  readonly insuranceType = input<InsuranceType | null>(null);

  /** Drops the "Aucun" entry: the host wants a real pick. */
  readonly required = input(true);

  protected readonly sentinel = NO_SIGNED_CONTRACT_DOCUMENT;

  constructor() {
    super();
    this.connect(
      combineLatest({
        familyMemberId: toObservable(this.familyMemberId).pipe(
          distinctUntilChanged()
        ),
        contractType: toObservable(this.contractType).pipe(
          distinctUntilChanged()
        ),
        insuranceType: toObservable(this.insuranceType).pipe(
          distinctUntilChanged()
        ),
      }),
      ({ familyMemberId, contractType, insuranceType }, query) =>
        familyMemberId
          ? this.documentService
              .searchSignedContractDocuments(familyMemberId, {
                contractType,
                insuranceType: insuranceType ?? undefined,
                searchText: query.trim(),
                limit: this.limit(),
              })
              .pipe(map((response) => response.data?.documents ?? []))
          : null
    );
  }

  protected isRequired(): boolean {
    return this.required();
  }

  protected searchReady(): boolean {
    return !!this.familyMemberId();
  }

  protected uploadMemberId(): string | null {
    return this.familyMemberId();
  }

  protected uploadType(): DocumentType {
    return UPLOAD_TYPES[this.contractType()];
  }

  protected itemId(document: SignedContractDocument): number {
    return document.documentId;
  }

  protected itemTitle(document: SignedContractDocument): string {
    return document.docName;
  }

  protected itemDetails(document: SignedContractDocument): string {
    return fmtDate(document.signedOn || document.createdOn);
  }
}
